import React, { useState, useEffect } from "react";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu when clicking on a link
  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "unset";
    };
  }, [isMenuOpen]);

  const navItems = [
    { id: "home", label: "Home" },
    // { id: "about", label: "About" },
    { id: "menu", label: "Menu" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <>
      <header
        className={`fixed w-full top-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg py-3"
            : "bg-white shadow-md py-4"
        }`}
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex justify-between items-center">
            {/* Logo with animation */}
            <div className="flex items-center group">
              <h1 className="text-2xl md:text-3xl font-display font-bold text-secondary relative">
                Food
                <span className="text-primary relative">
                  Palace
                  <span className="absolute -bottom-1 left-0 w-0 group-hover:w-full h-0.5 bg-gradient-to-r from-primary to-secondary transition-all duration-500"></span>
                </span>
              </h1>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item, index) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="relative px-4 py-2 text-secondary hover:text-primary transition-colors duration-300 font-body group"
                  onClick={handleNavClick}
                >
                  <span className="relative z-10">{item.label}</span>
                  {/* Hover underline animation */}
                  <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary transform -translate-x-1/2 group-hover:w-3/4 transition-all duration-500"></span>
                  {/* Background effect on hover */}
                  <span className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300"></span>
                </a>
              ))}
            </nav>

            {/* Mobile Menu Button - Animated Hamburger */}
            <button
              className="md:hidden relative w-10 h-10 flex flex-col items-center justify-center group z-50"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <span
                className={`absolute w-6 h-0.5 bg-secondary rounded-full transition-all duration-500 ${
                  isMenuOpen
                    ? "rotate-45 translate-y-0"
                    : "-translate-y-2 group-hover:-translate-y-1.5"
                }`}
              ></span>
              <span
                className={`absolute w-6 h-0.5 bg-secondary rounded-full transition-all duration-500 ${
                  isMenuOpen ? "opacity-0 translate-x-4" : "opacity-100"
                }`}
              ></span>
              <span
                className={`absolute w-6 h-0.5 bg-secondary rounded-full transition-all duration-500 ${
                  isMenuOpen
                    ? "-rotate-45 translate-y-0"
                    : "translate-y-2 group-hover:translate-y-1.5"
                }`}
              ></span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay - FIXED */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-all duration-300 ${
          isMenuOpen
            ? "opacity-100 visible"
            : "opacity-0 invisible"
        }`}
      >
        {/* Backdrop - FIXED: Removed pointer-events-none */}
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            isMenuOpen ? "opacity-50" : "opacity-0"
          }`}
          onClick={() => setIsMenuOpen(false)}
          onTouchStart={() => setIsMenuOpen(false)}
        ></div>

        {/* Menu Panel - FIXED: Simplified animation */}
        <div
          className={`absolute right-0 top-0 h-full w-64 sm:w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-out ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-secondary hover:text-primary transition-colors duration-300 z-50"
            onClick={() => setIsMenuOpen(false)}
            aria-label="Close menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Logo in Mobile Menu */}
          <div className="pt-16 px-6 pb-6">
            {/* <h2 className="text-xl font-display font-bold text-secondary">
              Food<span className="text-primary">Palace</span>
            </h2> */}
          </div>

          {/* Mobile Navigation Items - FIXED: Simplified animation */}
          <nav className="px-6 py-4">
            <div className="space-y-1">
              {navItems.map((item, index) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={handleNavClick}
                  className="block group"
                >
                  <div
                    className={`flex items-center py-3 px-4 rounded-lg hover:bg-gray-50 transition-all duration-300 ${
                      isMenuOpen
                        ? "opacity-100"
                        : "opacity-0"
                    }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <span className="text-base font-body font-medium text-secondary group-hover:text-primary transition-colors duration-300">
                      {item.label}
                    </span>
                    <svg
                      className="w-4 h-4 ml-auto text-gray-400 group-hover:text-primary transition-colors duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </a>
              ))}
            </div>
          </nav>
        </div>
      </div>

      {/* Global Styles */}
      <style jsx>{`
        /* Smooth scrolling for the whole page */
        html {
          scroll-behavior: smooth;
        }
        
        /* Fix for mobile touch scrolling */
        @media (max-width: 768px) {
          body.menu-open {
            position: fixed;
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

export default Header;